// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  ProfileRequestDTO,
  RequestActionDTO,
  CreateRequestPayload,
  VoidRequestPayload,
  ProxyCompletionPayload,
  ProfileRequestType,
  ProfileRequestStatus,
} from '../types/profileRequest';
import type { PageResponse } from '../types/relationship';

/**
 * Client for the Profile Requests REST endpoints (resource-server
 * `com.loa.controller.ProfileRequestController`).
 *
 * All endpoints are gated behind the `profile-requests.enabled` server
 * flag; requests fail with 404 when the flag is off — callers should
 * catch and surface a feature-unavailable state in that case.
 */
export const profileRequestApi = {
  list: async (
    profileId: string,
    opts: {
      requestType?: ProfileRequestType;
      status?: ProfileRequestStatus;
      page?: number;
      size?: number;
    } = {},
  ): Promise<PageResponse<ProfileRequestDTO>> => {
    const { data } = await api.get<PageResponse<ProfileRequestDTO>>(
      `/requests/profile/${profileId}`,
      {
        params: {
          requestType: opts.requestType,
          status: opts.status,
          page: opts.page ?? 0,
          size: opts.size ?? 50,
        },
      },
    );
    return data;
  },

  get: async (id: string): Promise<ProfileRequestDTO> => {
    const { data } = await api.get<ProfileRequestDTO>(`/requests/${id}`);
    return data;
  },

  create: async (payload: CreateRequestPayload): Promise<ProfileRequestDTO> => {
    const { data } = await api.post<ProfileRequestDTO>('/requests', payload);
    return data;
  },

  void: async (id: string, payload: VoidRequestPayload): Promise<ProfileRequestDTO> => {
    const { data } = await api.post<ProfileRequestDTO>(`/requests/${id}/void`, payload);
    return data;
  },

  nudge: async (id: string): Promise<void> => {
    await api.post(`/requests/${id}/nudge`);
  },

  /**
   * Send the per-policy provider nudge for every still-PENDING provider
   * row on a CoA Profile Request. 422 when the request type isn't CoA.
   */
  nudgeProvider: async (id: string): Promise<{ dispatchedCount: number }> => {
    const { data } = await api.post<{ dispatchedCount: number }>(
      `/requests/${id}/nudge-provider`,
    );
    return data;
  },

  /**
   * Download the signed PDF for a Profile Request — branches on
   * requestType server-side (LOA + CoA today).
   */
  downloadSignedDocument: async (id: string): Promise<Blob> => {
    const { data } = await api.get(`/requests/${id}/signed-document`, {
      responseType: 'blob',
    });
    return data;
  },

  listActions: async (id: string): Promise<RequestActionDTO[]> => {
    const { data } = await api.get<RequestActionDTO[]>(`/requests/${id}/actions`);
    return data;
  },

  proxyComplete: async (
    id: string,
    actionId: string,
    payload: ProxyCompletionPayload,
  ): Promise<RequestActionDTO> => {
    const { data } = await api.post<RequestActionDTO>(
      `/requests/${id}/actions/${actionId}/complete-by-proxy`,
      payload,
    );
    return data;
  },

  /**
   * Tenant-scoped count of non-terminal ProfileRequests grouped by
   * request type. Used by the Dashboard Pending Requests card.
   */
  pendingCountsByType: async (): Promise<Record<string, number>> => {
    const { data } = await api.get<Record<string, number>>('/requests/pending-counts-by-type');
    return data;
  },
};
